import React, { Component, lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import classNames from 'classnames';
import { Base } from '@fiverr-private/modal/src';
import { Button } from '@fiverr-private/fit';
import { I18n } from '@fiverr-private/fiverr_context';
import removeEmptyEntries from '../../../../../shared/utils/removeEmptyEntries';
import { AppContext } from '../../context/listingsContext';
import { getFiltersSelectedValues, getSelectedOptionValue } from '../../../utils/filter_builder';
import { clearObjectValues, convertStringToConstValue } from '../../../utils';
import { filterNavigation, sortNavigation } from '../../../service/clientNavigation';
import tracker from '../../../service/tracker';
import { FILTER } from '../../../config/filters';
import { SORT_BY, EVENTS_MAPPING, FILTERS_LOCATION } from '../../../utils/constants';
import LanguageSellerToggle from '../floating_topbar/menus/content/toggleFilters/language_seller_toggle';
import SubscriptionToggle from '../floating_topbar/menus/content/toggleFilters/subscription_toggle';
import LocalSellerToggle from '../floating_topbar/menus/content/toggleFilters/local_seller_toggle';
import { decideOnSellerToggleFilters } from '../seller_toggle_filter';
import SortContainer from './content/SortContainer';
import { MAX_GIG_PRICE_RANGE, MIN_GIG_PRICE_RANGE } from './Filter/consts';
import Filter from './Filter';
import '../filter/AdvancedSearch/index.scss';
import './index.scss';

const FilterWrapper = lazy(() => import(/* webpackChunkName: 'listings/FilterWrapper/FilterWrapper' */'./FilterWrapper'));

class MobileFilters extends Component {
    constructor(props, context) {
        super(props, context);
        const { filters, sorts } = props;
        this.initialFiltersValues = { ...getFiltersSelectedValues(filters) };
        const { assumedLanguage } = context;

        this.sellerToggleFilter = decideOnSellerToggleFilters(filters, { assumedLanguage });

        this.state = {
            filters: {},
            sort: sorts && getSelectedOptionValue(sorts)
        };
        this.applyModalFilters = this.applyModalFilters.bind(this);
        this.applyFilters = this.applyFilters.bind(this);
        this.clearAllFilters = this.clearAllFilters.bind(this);
        this.onChangeFilter = this.onChangeFilter.bind(this);
        this.toggleExportedFilter = this.toggleExportedFilter.bind(this);
        this.onClearFilter = this.onClearFilter.bind(this);
        this.generateFiltersToUrlValue = this.generateFiltersToUrlValue.bind(this);
        this.validatePriceRange = this.validatePriceRange.bind(this);
        this.onSortChange = this.onSortChange.bind(this);
        this.applySort = this.applySort.bind(this);
        this.sortFiltersPosition = this.sortFiltersPosition.bind(this);
    }

    sortFiltersPosition() {
        const filters = [...this.props.filters];
        const { exportedFilterId } = this.props;
        const customFilters = [];

        const sortedFilters = filters.reduce((acc, item) => {
            const filter = FILTER[item.id.toUpperCase()] || FILTER[convertStringToConstValue(item.id)];
            if (!filter) {
                customFilters.push(item);
                return acc;
            }
            acc[filter.SORT] = item;
            return acc;
        }, []);

        sortedFilters.splice(FILTER.GIG_PRICE_RANGE.SORT + 1, 0, ...customFilters);

        return sortedFilters.filter(({ id }) => id !== exportedFilterId);
    }

    generateFiltersToUrlValue(filters) {
        if (filters.gig_price_range) {
            filters.gig_price_range = this.validatePriceRange(filters.gig_price_range);
        }

        if (filters[FILTER.SELLER_LANGUAGE.ID]) {
            filters[FILTER.SELLER_LANGUAGE.ID] = [filters[FILTER.SELLER_LANGUAGE.ID]];
        }

        if (filters[FILTER.SELLER_LOCATION.ID]) {
            filters[FILTER.SELLER_LOCATION.ID] = [filters[FILTER.SELLER_LOCATION.ID]];
        }

        return Object.keys(filters).reduce((acc, filterId) => {
            if (!filters[filterId]) {
                return acc;
            }
            const id = filterId.toUpperCase();
            if (FILTER[id] && FILTER[id].SELECTED_VALUE) {
                acc[filterId] = FILTER[id].SELECTED_VALUE;
            }
            return acc;
        }, filters);
    }

    validatePriceRange(value) {
        let [ min, max ] = value;

        if (max && min > max) {
            max = value[0];
            min = value[1];
        }

        return [min || MIN_GIG_PRICE_RANGE, max || MAX_GIG_PRICE_RANGE];
    }

    applyModalFilters(close) {
        this.applyFilters();
        close();
    }

    applyFilters() {
        const { activeFilters } = this.props;
        const filters = this.generateFiltersToUrlValue({ ...this.state.filters });
        if (!_.isEmpty(filters)) {
            !_.isEqual(removeEmptyEntries({ ...filters }), removeEmptyEntries({ ...activeFilters })) &&
            filterNavigation(filters, activeFilters);
        }
    }

    applySort(close) {
        sortNavigation({ [SORT_BY]: this.state.sort });
        close();
    }

    clearAllFilters() {
        let currentFilters = { ...this.state.filters };
        currentFilters = { ...clearObjectValues(this.initialFiltersValues), ...clearObjectValues(currentFilters) };

        delete currentFilters[this.props.exportedFilterId];

        this.setState({ filters: currentFilters });
    }

    onFilterOpen(open) {
        tracker.trackMobileFilterOpen();
        tracker.click(FILTERS_LOCATION.MOBILE_FILTER);
        open();
    }

    onSortOpen(open) {
        tracker.click(`${EVENTS_MAPPING.CLICK.TARGETS.TOP_FILTER}${FILTERS_LOCATION.SORT_BY}`);
        open();
    }

    onChangeFilter(id, value) {
        const filtersValues = { ...this.state.filters };
        filtersValues[id] = value;
        this.setState({ filters: filtersValues });
    }

    onSortChange({ target }) {
        this.setState({ sort: target.value });
    }

    onToggleChange = (filter) => {
        filterNavigation(
            this.generateFiltersToUrlValue(filter),
            this.props.activeFilters,
            FILTERS_LOCATION.FLOATING_TOP_RIGHT
        );
    }

    onClearFilter(id, value) {
        const currentValues = { ...this.state.filters };
        currentValues[id] = value;
        this.setState({ filters: currentValues });
    }

    toggleExportedFilter(id, value) {
        const { exportedFilterId, activeFilters } = this.props;
        this.onChangeFilter(id, value);

        this.setState((prevState) => ({ filters: { ...prevState.filters, [exportedFilterId]: value } }), () => {
            filterNavigation(this.generateFiltersToUrlValue({ ...this.state.filters, [exportedFilterId]: value }), activeFilters, FILTERS_LOCATION.TOP);
        });
    }

    render() {
        const { filters: currentFilters, sort } = this.state;
        const { activeFilters, filters, sorts, exportedFilterId } = this.props;
        const activeCount = Object.keys(activeFilters).length;
        const displayFilters = this.sortFiltersPosition();

        if (!filters.length) {
            return null;
        }
        const SellerToggleFilter = this.sellerToggleFilter && this.sellerToggleFilter.id === FILTER.SELLER_LOCATION.ID ?
            LocalSellerToggle :
            LanguageSellerToggle;

        const subscription = filters.find(({ id }) => id === FILTER.SUBSCRIPTION.ID);
        const exportedFilter = filters.find(({ id }) => id === exportedFilterId);
        const filterValues = { ...this.initialFiltersValues, ...currentFilters };

        return (
            <AppContext.Consumer>
                {({ currency }) => (
                    <div className="filter-button-wrapper p-b-8">
                        <div className="layout-row filters-container">
                            <Base name="search_perseus listings-perseus filter-modal"
                                trigger={({ open }) => (
                                    <Button className={classNames('touch-filter', { 'full-width': !sorts })}
                                        onClick={() => this.onFilterOpen(open)}>
                                        <I18n k={'search_perseus.filter.filter'}/>
                                        {activeCount > 0 && <span>{`(${activeCount})`}</span>}
                                    </Button>
                                )}>
                                {({ close }) => (
                                    <Suspense fallback={<div/>}>
                                        <FilterWrapper filters={displayFilters}
                                            values={filterValues}
                                            currency={currency}
                                            close={close}
                                            clearAll={this.clearAllFilters}
                                            applyFilters={this.applyModalFilters}
                                            onChange={this.onChangeFilter}
                                            onClear={this.onClearFilter}/>
                                    </Suspense>
                                )}
                            </Base>
                            {sorts &&
                                <Base name="search_perseus listings-perseus filter-modal"
                                    trigger={({ open }) => (
                                        <Button className="sort-button"
                                            onClick={() => this.onSortOpen(open)}>
                                            <I18n k="search_perseus.filter.sort"/>
                                        </Button>
                                    )}>
                                    {({ close }) => (
                                        <Suspense fallback={<div/>}>
                                            <SortContainer onChange={this.onSortChange}
                                                applyFilters={this.applySort}
                                                values={sort}
                                                close={close}
                                                sorts={sorts}/>
                                        </Suspense>
                                    )}
                                </Base>
                            }
                        </div>
                        {exportedFilter &&
                            <div className="exported-filter-toggle">
                                <Filter filter={exportedFilter}
                                    value={filterValues[exportedFilter.id]}
                                    onChange={this.toggleExportedFilter}
                                    onClear={this.onClearFilter}/>
                            </div>}

                        { (subscription && !exportedFilter) &&
                        <SubscriptionToggle className={classNames('layout-row', 'switch-container')}
                            onCustomChange={this.onToggleChange}
                            filter={subscription}/>
                        }

                        { (!subscription && !exportedFilter && SellerToggleFilter) &&
                        <SellerToggleFilter className={classNames('layout-row', 'switch-container')}
                            onCustomChange={this.onToggleChange}
                            filter={this.sellerToggleFilter}/>
                        }
                    </div>
                )}
            </AppContext.Consumer>
        );
    }
}

MobileFilters.contextType = AppContext;

MobileFilters.propTypes = {
    activeFilters: PropTypes.object,
    filters: PropTypes.array.isRequired,
    sorts: PropTypes.array,
    exportedFilterId: PropTypes.string
};

export default MobileFilters;
